import { useState, useEffect } from 'react';
import { getComments, reactToComment, Comment } from '../lib/api';
import { ReactionType, REACTIONS } from '../types/confession';
import { hapticFeedback } from '../lib/telegram';

interface CommentsProps {
  confessionId: string;
  onOpenModal?: (confessionId: string, commentCount: number) => void;
}

const PREVIEW_LIMIT = 3;

function CommentItem({ comment }: { comment: Comment }) {
  const [isReacting, setIsReacting] = useState(false);
  const [localReactions, setLocalReactions] = useState(comment.reactions);
  const [userReactions, setUserReactions] = useState<ReactionType[]>(
    comment.userReactions || []
  );

  const handleReaction = async (reactionType: ReactionType) => {
    if (isReacting) return;
    
    hapticFeedback('light');
    setIsReacting(true);

    const hasReacted = userReactions.includes(reactionType);
    
    // Optimistic update
    setLocalReactions(prev => ({
      ...prev,
      [reactionType]: prev[reactionType] + (hasReacted ? -1 : 1),
    }));
    
    setUserReactions(prev =>
      hasReacted
        ? prev.filter(r => r !== reactionType)
        : [...prev, reactionType]
    );

    try {
      const result = await reactToComment(comment.id, reactionType, hasReacted);
      setLocalReactions(result.reactions);
    } catch (error) {
      // Revert on error
      setLocalReactions(comment.reactions);
      setUserReactions(comment.userReactions || []);
    } finally {
      setIsReacting(false);
    }
  };

  // Format relative time
  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const totalReactions = Object.values(localReactions).reduce((a, b) => a + b, 0);

  return (
    <div className="bg-white/5 rounded-lg p-3">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xs">🤖</span>
        <span className="text-sm font-medium text-purple-400">{comment.agentName}</span>
        <span className="text-xs text-gray-500">{formatTime(comment.createdAt)}</span>
      </div>
      <p className="text-sm text-gray-200 mb-2">{comment.text}</p>
      
      {/* Compact reaction buttons */}
      <div className="flex items-center gap-1">
        {REACTIONS.map(({ type, emoji }) => {
          const count = localReactions[type] || 0;
          const isActive = userReactions.includes(type);
          
          return (
            <button
              type="button"
              key={type}
              onClick={() => handleReaction(type)}
              disabled={isReacting}
              className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs transition-all duration-150 ${
                isActive
                  ? 'bg-white/20'
                  : 'bg-white/5 hover:bg-white/10'
              }`}
            >
              <span className="text-xs">{emoji}</span>
              {count > 0 && (
                <span className="text-[10px] tabular-nums text-gray-400">{count}</span>
              )}
            </button>
          );
        })}
        {totalReactions > 0 && (
          <span className="text-[10px] text-gray-500 ml-1">{totalReactions}</span>
        )}
      </div>
    </div>
  );
}

export function Comments({ confessionId, onOpenModal }: CommentsProps) {
  const [previewComments, setPreviewComments] = useState<Comment[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  // Fetch preview comments (top 3 by reactions) and count
  useEffect(() => {
    setIsLoading(true);
    getComments(confessionId, { sort: 'top', limit: PREVIEW_LIMIT })
      .then(data => {
        setPreviewComments(data.comments);
        setTotalCount(data.totalCount);
      })
      .catch(console.error)
      .finally(() => setIsLoading(false));
  }, [confessionId]);

  const handleToggleExpand = () => {
    hapticFeedback('light');
    setIsExpanded(!isExpanded);
  };

  const handleViewAll = () => {
    console.log('[Comments] handleViewAll called, onOpenModal:', !!onOpenModal, 'confessionId:', confessionId, 'totalCount:', totalCount);
    hapticFeedback('medium');
    if (onOpenModal) {
      onOpenModal(confessionId, totalCount);
    } else {
      console.warn('[Comments] onOpenModal is not defined!');
    }
  };

  if (totalCount === 0 && !isLoading) {
    return null; // Don't show anything if no comments
  }

  const hasMoreComments = totalCount > PREVIEW_LIMIT;

  return (
    <>
      <div className="mt-3 pt-3 border-t border-white/10">
        {/* Collapsible header */}
        <button
          onClick={handleToggleExpand}
          className="flex items-center gap-2 text-sm text-purple-400 hover:text-purple-300 transition-colors w-full"
        >
          <span>💬</span>
          <span>{totalCount} comment{totalCount !== 1 ? 's' : ''}</span>
          <span className="text-xs ml-auto">{isExpanded ? '▲' : '▼'}</span>
        </button>

        {/* Preview comments when expanded */}
        {isExpanded && (
          <div className="mt-3 space-y-2">
            {isLoading ? (
              <div className="text-sm text-gray-500">Loading comments...</div>
            ) : previewComments.length === 0 ? (
              <div className="text-sm text-gray-500">No comments yet</div>
            ) : (
              <>
                {previewComments.map(comment => (
                  <CommentItem key={comment.id} comment={comment} />
                ))}
                
                {/* View all button */}
                {hasMoreComments && (
                  <button
                    onClick={handleViewAll}
                    className="w-full py-2 text-sm text-purple-400 hover:text-purple-300 bg-white/5 hover:bg-white/10 rounded-lg transition-all flex items-center justify-center gap-1"
                  >
                    View all {totalCount} comments
                    <span className="text-xs">→</span>
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>

    </>
  );
}
